/**
 * The phone Planner's week (spec 2026-10-14 §5, §9): seven DayCells with initials, the week that holds
 * the picked day. A sideways swipe pages it. The PanResponder claims a MOVE only once it is sideways
 * enough and more sideways than down, so taps reach the cells and a vertical drag still scrolls the
 * page; a release past SWIPE_DISTANCE (the travel from the touch's start, since PanResponder restarts
 * dx at the claim) asks the screen to move the selection a week, and the new week slides in from the
 * side it came from. Not a paging ScrollView: react-native-web 0.21 fires no scroll-end event to read
 * the page from.
 */
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Animated, Easing, PanResponder, StyleSheet, View } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useMotion } from '@/hooks/shared/useMotion';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { getWeekDays, toDateKey } from '@/utils/residents/todoCalendar';
import { DayCell } from './DayCell';

/** How far a sideways drag must travel before its release pages the week. */
export const SWIPE_DISTANCE = 40;
/** A move is claimed as a swipe once it is this far sideways (and more sideways than down). */
const CLAIM_DISTANCE = 12;
/** How far off to the side a new week starts as it slides in. */
const SLIDE_FROM = 24;

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** The page a release asks for: 1 (next week), -1 (previous), or 0. */
export const swipeStep = (dx: number, dy: number): -1 | 0 | 1 =>
  Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) <= Math.abs(dy) ? 0 : dx < 0 ? 1 : -1;

/** Whether a move is a swipe rather than a tap or a vertical scroll. */
export const claimsSwipe = (dx: number, dy: number): boolean =>
  Math.abs(dx) > CLAIM_DISTANCE && Math.abs(dx) > Math.abs(dy);

export interface PlannerWeekStripProps {
  selectedDate: Date;
  todayKey: string;
  /** Remaining to-dos per date key (the dots and the cells' labels). */
  countsByKey: Record<string, number>;
  onSelectDay: (d: Date) => void;
  /** A swipe: move the selection a week. */
  onWeekChange: (step: -1 | 1) => void;
}

export const PlannerWeekStrip: React.FC<PlannerWeekStripProps> = ({
  selectedDate,
  todayKey,
  countsByKey,
  onSelectDay,
  onWeekChange,
}) => {
  const motion = useMotion();
  const reduceMotion = useReducedMotion();
  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);
  const weekKey = toDateKey(weekDays[0]);
  const selectedKey = toDateKey(selectedDate);

  // -1..1: where the week sits as it slides in (0 = home). Only a CHANGE of week starts a slide, from
  // the side the new week lies on (date keys sort as dates).
  const slide = useRef(new Animated.Value(0)).current;
  const shownWeek = useRef(weekKey);
  // Before paint, so the new week never shows at rest first.
  useLayoutEffect(() => {
    if (shownWeek.current === weekKey) return;
    const from = weekKey > shownWeek.current ? 1 : -1;
    shownWeek.current = weekKey;
    if (reduceMotion) {
      slide.setValue(0);
      return;
    }
    slide.setValue(from);
    const settle = Animated.timing(slide, {
      toValue: 0,
      duration: motion.duration.fast,
      easing: Easing.bezier(...motion.easing.standard),
      useNativeDriver: true,
    });
    settle.start();
    return () => settle.stop();
  }, [weekKey, reduceMotion, slide, motion]);

  // The responder is made once; the latest callback rides a ref. So does the travel seen when a move
  // was claimed: PanResponder restarts dx/dy there, and a release adds it back.
  const onWeekChangeRef = useRef(onWeekChange);
  onWeekChangeRef.current = onWeekChange;
  const claimedAt = useRef({ dx: 0, dy: 0 });
  const responder = useMemo(() => {
    const forgetClaim = () => {
      claimedAt.current = { dx: 0, dy: 0 };
    };
    return PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => {
        const claims = claimsSwipe(g.dx, g.dy);
        if (claims) claimedAt.current = { dx: g.dx, dy: g.dy };
        return claims;
      },
      onPanResponderRelease: (_e, g) => {
        const step = swipeStep(claimedAt.current.dx + g.dx, claimedAt.current.dy + g.dy);
        forgetClaim();
        if (step !== 0) onWeekChangeRef.current(step);
      },
      onPanResponderTerminate: forgetClaim,
      onPanResponderTerminationRequest: () => true,
    });
  }, []);

  const styles = useThemedStyles(() =>
    StyleSheet.create({
      row: {
        flexDirection: 'row',
      },
    }),
  );

  return (
    <View testID="planner-week-strip" {...responder.panHandlers}>
      <Animated.View
        style={[
          styles.row,
          {
            opacity: slide.interpolate({ inputRange: [-1, 0, 1], outputRange: [0, 1, 0] }),
            transform: [
              {
                translateX: slide.interpolate({
                  inputRange: [-1, 0, 1],
                  outputRange: [-SLIDE_FROM, 0, SLIDE_FROM],
                }),
              },
            ],
          },
        ]}
      >
        {weekDays.map((day, index) => {
          const key = toDateKey(day);
          return (
            <DayCell
              key={key}
              day={day}
              weekdayInitial={WEEKDAY_INITIALS[index]}
              isToday={key === todayKey}
              isSelected={key === selectedKey}
              count={countsByKey[key] ?? 0}
              onPress={onSelectDay}
            />
          );
        })}
      </Animated.View>
    </View>
  );
};

PlannerWeekStrip.displayName = 'PlannerWeekStrip';
